import React, { useState } from 'react';
import { ClipboardList, ScrollText } from 'lucide-react';
import { useIsMobile } from '../../helpers/useIsMobile';
import { useTouchLayout } from './receivingLayout';
import { ReceivingProvider, useReceivingStore } from './ReceivingStore';
import ReceivingQueueTab from './ReceivingQueueTab';
import ReceivingScanLogTab from './ReceivingScanLogTab';

const TABS = {
  QUEUE: 'queue',
  SCAN_LOG: 'scan-log',
};

function BottomTabBar({ activeTab, onTabChange }) {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-50 flex items-end justify-around border-t border-gray-200 bg-white"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 8px)' }}
    >
      <button
        type="button"
        onClick={() => onTabChange(TABS.QUEUE)}
        className={`flex flex-1 flex-col items-center gap-0.5 pb-1 pt-2.5 text-sm font-medium transition-colors ${
          activeTab === TABS.QUEUE ? 'text-gray-900' : 'text-gray-500'
        }`}
      >
        <ClipboardList className="h-5 w-5" strokeWidth={activeTab === TABS.QUEUE ? 2.2 : 1.8} />
        <span>Queue</span>
      </button>
      <button
        type="button"
        onClick={() => onTabChange(TABS.SCAN_LOG)}
        className={`flex flex-1 flex-col items-center gap-0.5 pb-1 pt-2.5 text-sm font-medium transition-colors ${
          activeTab === TABS.SCAN_LOG ? 'text-gray-900' : 'text-gray-500'
        }`}
      >
        <ScrollText className="h-5 w-5" strokeWidth={activeTab === TABS.SCAN_LOG ? 2.2 : 1.8} />
        <span>Scan Log</span>
      </button>
    </nav>
  );
}

function ReceivedSessionsContent({ scanLogRefresh }) {
  const { pos } = useReceivingStore();
  const isMobile = useIsMobile();
  const touchLayout = useTouchLayout();
  const [activeTab, setActiveTab] = useState(TABS.QUEUE);
  const [queueCount, setQueueCount] = useState(null);
  // Bump on each tab click so the panel remounts and reloads like first visit
  const [tabMountKey, setTabMountKey] = useState(0);

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setTabMountKey((k) => k + 1);
    if (tab === TABS.QUEUE) setQueueCount(null);
  };

  const openCount =
    queueCount ??
    pos.filter((p) => p.eadinStatus !== 'closed' && p.eadinStatus !== 'canceled').length;

  return (
    <div className="flex flex-col bg-gray-50" style={{ height: 'calc(100vh - 48px)' }}>
      <header className="shrink-0 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between gap-4 px-5 pb-0 pt-3">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-gray-900">Receiving</h1>
            {!(touchLayout && activeTab === TABS.QUEUE) && (
              <p className="mt-0.5 truncate text-sm text-gray-500">
                {activeTab === TABS.SCAN_LOG
                  ? 'Every RFID scan session — raw reads, deduplicated EPCs, and match results'
                  : (
                    <>
                      Inbound POs, box-level RFID scans, and exception triage ·{' '}
                      <span className="tabular-nums">{openCount}</span> in queue
                    </>
                  )}
              </p>
            )}
          </div>
          <div className="hidden shrink-0 items-center gap-2 md:flex">
            {/* <button
              type="button"
              onClick={() =>
                toast.info('Scan session started — open a PO and scan a box from the inspector.', { position: 'bottom-right' })
              }
              className="h-8 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              Start scan session
            </button>
            <Link
              to={ROUTES.PURCHASE_ORDERS}
              className="inline-flex h-8 items-center gap-1 rounded-md bg-gray-900 px-3 text-sm font-medium text-white hover:bg-gray-800"
            >
              <Plus className="h-3.5 w-3.5" />
              Expected receipt
            </Link> */}
          </div>
        </div>

        <div className="mt-2 hidden gap-0.5 px-5 md:flex">
          {[
            { key: TABS.QUEUE, label: 'Queue' },
            { key: TABS.SCAN_LOG, label: 'Scan Log' },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => handleTabChange(tab.key)}
              className={`-mb-px border-b-2 px-3 pb-2 pt-1 text-base font-medium transition-colors ${
                activeTab === tab.key
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      <div className={`flex min-h-0 flex-1 flex-col overflow-hidden ${isMobile ? 'pb-[64px]' : ''}`}>
        {activeTab === TABS.QUEUE ? (
          <ReceivingQueueTab
            key={`queue-${tabMountKey}`}
            refreshKey={tabMountKey}
            onFilteredCountChange={setQueueCount}
          />
        ) : (
          <ReceivingScanLogTab
            key={`scan-log-${tabMountKey}`}
            refreshKey={tabMountKey + scanLogRefresh}
          />
        )}
      </div>

      {isMobile && <BottomTabBar activeTab={activeTab} onTabChange={handleTabChange} />}
    </div>
  );
}

function ReceivedSessions() {
  const [scanLogRefresh, setScanLogRefresh] = useState(0);

  return (
    <ReceivingProvider onSessionSaved={() => setScanLogRefresh((k) => k + 1)}>
      <ReceivedSessionsContent scanLogRefresh={scanLogRefresh} />
    </ReceivingProvider>
  );
}

export default ReceivedSessions;
